import { IonButton, IonInput, IonItem } from '@ionic/react';
import { Modal } from 'antd';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { isMobileDevice } from '../../config/utils/deviceUtils';
import { ErrorForm } from '../atoms/ErrorForm';

interface Props {
  visible: boolean;
  initialEmail?: string | null;
  onCancel: () => void;
  onSubmit: (email: string) => Promise<void> | void;
}

const schema = yup.object({
  email: yup
    .string()
    .email('FORMS.ERRORS.INVALID_EMAIL')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'FORMS.ERRORS.INVALID_EMAIL')
    .required('FORMS.ERRORS.REQUIRED_EMAIL'),
});

export const EmailModal: React.FC<Props> = ({ visible, initialEmail, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const inputRef = useRef<any>(null);
  const isMobile = useMemo(() => isMobileDevice(), []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: initialEmail ?? '',
    },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onSubmit(values.email);
        onCancel();
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    // focus input shortly after open
    if (visible) {
      const time = setTimeout(() => {
        inputRef.current?.setFocus?.();
      }, 80);
      return () => clearTimeout(time);
    }
  }, [visible]);

  // Reset form when modal is closed
  useEffect(() => {
    if (!visible) {
      formik.resetForm();
    }
  }, [visible]);

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered={!isMobile}
      width={isMobile ? '100%' : 520}
      style={{ top: 0, padding: 0 }}
      styles={{ body: isMobile ? { height: 'calc(100vh - 56px)', overflow: 'auto' } : undefined }}
      destroyOnClose>
      <div className="flex-start-start-column-nowrap padding-16" style={{ height: '100%' }}>
        <div className="mediumText6 margin-b-16">{t('FORMS.SEND_DOCUMENT_BY_EMAIL')}</div>

        <div className="margin-b-40">
          <div className="padding-b-16 normalText4">{t('FORMS.FILL_EMAIL')}</div>
          <div className="flex-center-start-column-nowrap positionRelative width-300px">
            <IonItem className="width-300px height-50px border-radius-input g-select-border cursorPointer" mode="md">
              <IonInput
                ref={inputRef}
                name="email"
                placeholder="name@example.com"
                className="height-48px"
                mode="md"
                type="email"
                aria-label="email"
                onIonInput={formik.handleChange}
                value={formik.values.email}
                onKeyDown={(e: any) => {
                  // Handle Enter key (works for desktop and mobile soft keyboards).
                  // Use any typing because IonInput events can differ across platforms.
                  const key = e?.key ?? e?.detail?.key ?? '';
                  if (key === 'Enter' || e?.keyCode === 13) {
                    e.preventDefault?.();
                    formik.handleSubmit();
                  }
                }}
                enterkeyhint={isMobile ? 'send' : undefined}
              />
            </IonItem>
            <ErrorForm styleName="margin-t-minus-14">
              {formik.touched.email ? t(formik.errors.email || '') : ''}
            </ErrorForm>
          </div>
        </div>

        <div className="flex-end-center-row-nowrap gap-10 width-100">
          <IonButton mode="md" onClick={onCancel} disabled={formik.isSubmitting}>
            {t('FORMS.CANCEL')}
          </IonButton>
          <IonButton
            mode="md"
            onClick={() => formik.handleSubmit()}
            disabled={!!formik.errors.email || formik.isSubmitting}>
            {t('FORMS.SEND')}
          </IonButton>
        </div>
      </div>
    </Modal>
  );
};
